import classnames from 'classnames';
import React, { useMemo } from 'react';

import AdPosition from 'common/components/AdPosition';
import BookingButton from 'common/components/ShowtimeModal/BookingButton';
import BookingPremium from 'common/components/ShowtimeModal/BookingPremium';
import useShowtimeData from 'common/components/ShowtimeModal/hooks/useShowtimeData';
import { BRAND_HAS_DISPLAY_DOLBY_CINEMA_SHOWTIME_UPSELL } from 'common/configuration/constants';
import { AD_NATIVE_BOOKING } from 'common/constants/Ads';
import {
  HAS_DOLBY_UPSALE,
  TheaterReservationTracking
} from 'common/constants/trackingEventsNames';
import { ModalNames } from 'common/module/ui/lazy-modal/constant-types';
import buildReactDomModalModule from 'common/module/ui/lazy-modal/react-dom-modal-module';
import ReactModal from 'common/module/ui/lazy-modal/react-modal';
import { CloseModal } from 'common/module/ui/lazy-modal/types';
import format from 'common/tools/date/format';
import getRuntime from 'common/tools/showtime/getRuntime';

import MovieSummary from './MovieSummary';
import QuickInformations from './QuickInformations';
import TheaterCover from './TheaterCover';
import styles from './styles.module.scss';

export type ShowtimeModalParams = {
  fallback?: (() => void) | null;
  showtimeId: number;
  trackingContext: TheaterReservationTracking;
};

type ShowtimeModalProps = ShowtimeModalParams & {
  closeModal: CloseModal;
};

const ShowtimeModal = ({
  closeModal,
  fallback,
  showtimeId,
  trackingContext
}: ShowtimeModalProps) => {
  const {
    data: {
      date,
      experiences,
      loading,
      movie,
      showtime,
      theater,
      ticketingUrl
    },
    proposal,
    trackingData
  } = useShowtimeData(showtimeId, trackingContext);

  const showtimeDate = useMemo(
    () =>
      date
        ? format(date, {
            weekday: 'long',
            day: '2-digit',
            month: 'long'
          })
        : '',
    [date]
  );

  const movieRuntime = useMemo(
    () => getRuntime(movie?.runtime),
    [movie?.runtime]
  );

  return (
    <ReactModal
      title={showtimeDate}
      className={classnames(styles.modal, { [styles.loading]: loading })}
      size="medium"
      fallback={fallback}
    >
      <div className="hred">
        <QuickInformations
          experiences={experiences}
          date={date}
          showtime={showtime}
          movieRuntime={movieRuntime}
        />
      </div>
      <div className="hred">
        <TheaterCover theater={theater} />
      </div>
      <div className="hred">
        <MovieSummary movie={movie} movieRuntime={movieRuntime} />
      </div>
      <BookingButton
        closeModal={closeModal}
        experiences={experiences}
        loading={loading}
        ticketingUrl={ticketingUrl}
        trackingContext={trackingData.current}
      />
      {BRAND_HAS_DISPLAY_DOLBY_CINEMA_SHOWTIME_UPSELL && (
        <BookingPremium
          proposal={proposal}
          closeModal={closeModal}
          loading={loading}
          trackingContext={{
            ...trackingData.current,
            showtime: { ...trackingContext['showtime'], ...proposal },
            ga4dataOverwrite: {
              has_dolby: HAS_DOLBY_UPSALE
            }
          }}
        />
      )}

      {date && showtimeDate && !loading ? (
        <AdPosition
          classnames={styles.adSection}
          position={AD_NATIVE_BOOKING}
        />
      ) : null}
    </ReactModal>
  );
};

export default buildReactDomModalModule(
  ModalNames.SHOWTIME_MODAL,
  (params: ShowtimeModalParams, closeModal: CloseModal) => (
    <ShowtimeModal closeModal={closeModal} {...params} />
  )
);
